import React, { useMemo } from 'react';
import type { WeeklyHeatmapWeek } from '@troia-v3/types';

import { DashCard } from '@/shared/components/ui';

interface WeeklyHeatmapCardProps {
  weeks: WeeklyHeatmapWeek[];
}

const formatMil = (v: number): string => {
  if (v === 0) return '—';
  return `${Math.round(v / 1000)}`;
};

const isoWeek = (isoDate: string): number => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

/** Rampa de intensidade sobre o primário do tema (F63 — sem roxo hardcoded). */
const primaryMix = (pct: number): string =>
  `color-mix(in srgb, var(--theme-primary) ${pct}%, transparent)`;

const bandStyle = (v: number, max: number): { bg: string; text: string } => {
  if (v === 0) return { bg: 'transparent', text: 'var(--theme-muted-text)' };
  const intensity = max === 0 ? 0 : Math.min(1, v / max);
  if (intensity > 0.72) return { bg: primaryMix(85), text: '#fff' };
  if (intensity > 0.5) return { bg: primaryMix(58), text: '#fff' };
  if (intensity > 0.28) return { bg: primaryMix(34), text: 'var(--theme-card-text)' };
  return { bg: primaryMix(16), text: 'var(--theme-card-text)' };
};

const DAY_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex'];

export const WeeklyHeatmapCard: React.FC<WeeklyHeatmapCardProps> = ({ weeks }) => {
  const maxValue = useMemo(() => {
    let max = 0;
    for (const w of weeks) {
      for (const d of w.days) max = Math.max(max, d.revenue);
    }
    return max;
  }, [weeks]);

  return (
    <DashCard
      icon="grid"
      title="Receita por dia da semana"
      subtitle="Padrão semanal de fechamento"
      testId="weekly-heatmap-card"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left py-1 px-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--theme-muted-text)' }} />
              {DAY_LABELS.map((d) => (
                <th
                  key={d}
                  className="text-center py-1 px-2 text-[10px] font-bold uppercase tracking-wider"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  {d}
                </th>
              ))}
              <th
                className="text-right py-1 px-2 text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {weeks.length === 0 && (
              <tr>
                <td colSpan={7} className="py-4 text-center" style={{ color: 'var(--theme-muted-text)' }}>
                  Sem dados no período.
                </td>
              </tr>
            )}
            {weeks.map((w) => (
              <tr key={w.weekStart} data-testid={`heatmap-week-${w.weekStart}`}>
                <td className="py-1 px-2 text-[10px] font-semibold" style={{ color: 'var(--theme-muted-text)' }}>
                  Sem {isoWeek(w.weekStart)}
                </td>
                {DAY_LABELS.map((_, idx) => {
                  const day = w.days.find((d) => d.dayOfWeek === idx + 1);
                  const v = day?.revenue ?? 0;
                  const band = bandStyle(v, maxValue);
                  return (
                    <td key={idx} className="py-1 px-1">
                      <div
                        className="h-[34px] rounded-[7px] flex items-center justify-center font-semibold"
                        style={{
                          backgroundColor: band.bg,
                          color: band.text,
                        }}
                      >
                        {formatMil(v)}
                      </div>
                    </td>
                  );
                })}
                <td className="py-1 px-1">
                  <div
                    className="h-[34px] rounded-[7px] flex items-center justify-center font-bold"
                    style={{ backgroundColor: 'var(--theme-card-border)', color: 'var(--theme-card-text)' }}
                  >
                    {formatMil(w.total)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[11px]" style={{ color: 'var(--theme-muted-text)' }}>
        Intensidade por receita · valores em R$ mil · dias úteis (últimas 4 semanas)
      </div>
    </DashCard>
  );
};

WeeklyHeatmapCard.displayName = 'WeeklyHeatmapCard';
